import { useMutation } from '@tanstack/react-query'
import { AxiosError } from 'axios'
import { ServerCrash, UserRoundPen } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'

import { Toast } from '@/common/components/Toast'
import { Gender } from '@/common/models/person'
import { UserModel } from '@/common/models/user'
import { UserPreferenceModel } from '@/common/models/userPreference'
import { api, ApiError } from '@/lib/axios'
import { queryClient } from '@/lib/tanstack'
import { EditUserForm } from '../schemas/EditUserSchema'
import { useSelectedUserStore } from '../stores/useSelectedUserStore'

type PutEditUserProps = UserModel & {
  person: {
    email: string
    genre: Gender
    isForeign: boolean
    birthDate?: Date
    documentNumber?: string
    name?: string
  }
  userPreference: UserPreferenceModel
}

async function putEditUser({ id, personId, ...data }: PutEditUserProps) {
  const { person, userPreference, ...user } = data

  return Promise.all([
    api.backend.put(`/user/${id}`, user),
    api.backend.put(`/person/${personId}`, {
      genre: person.genre,
      isForeign: person.isForeign,
      birthDate: person.birthDate,
      documentNumber: person.documentNumber,
      name: person.name,
      email: person.email,
    }),
    api.backend.put(`/user-preference/${userPreference.id}`, userPreference),
  ])
}

export function useEditUser() {
  const { t } = useTranslation('registers')
  const { user } = useSelectedUserStore()

  const { mutateAsync, isPending } = useMutation<
    unknown,
    AxiosError<ApiError>,
    PutEditUserProps
  >({
    mutationKey: ['/user/id/edit'],
    mutationFn: putEditUser,
    onSuccess: () => {
      toast(Toast, {
        data: {
          type: 'success',
          icon: UserRoundPen,
          title: t('users.drawer_edit_user.toast_success.title'),
        },
      })
      queryClient.invalidateQueries({ queryKey: ['/user/paginate'] })
    },
    onError: (error) => {
      toast(Toast, {
        data: {
          type: 'danger',
          icon: ServerCrash,
          title: t('users.drawer_edit_user.toast_error.title'),
          description:
            error?.response?.data?.friendlyMessage ||
            t('users.drawer_edit_user.toast_error.description'),
        },
      })
    },
  })

  const handleEditUser = async (data: EditUserForm) => {
    if (!user) return

    const {
      email,
      editAllowedPosts,
      birthDate,
      displayName,
      documentNumber,
      genre,
      isForeign,
      name,
      userType,
    } = data

    const { Person, UserPreference, ...userData } = user

    const payload: PutEditUserProps = {
      ...userData,
      userType,
      userPreference: {
        ...UserPreference,
        allowedPosts: editAllowedPosts,
        displayName,
      },
      person: {
        name,
        email,
        documentNumber,
        birthDate,
        genre,
        isForeign,
      },
    }

    return mutateAsync(payload)
  }

  return { handleEditUser, isEditingUser: isPending }
}
